import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTabContext } from '@/contexts/TabContext';

interface UseTabTitleSyncOptions {
  /** Disable tab title sync (e.g., in split view mode) */
  disabled?: boolean;
}

/**
 * Hook to synchronize the tab title with the loaded item name.
 * 
 * This hook should be used in detail pages (e.g., LibraryDetailPage, DocumentDetailPage)
 * to update the tab title once the item data is loaded. This ensures that tab titles
 * always show human-readable item names instead of UUIDs/IDs.
 * 
 * @param itemName - The name of the item (e.g., from item.name)
 * @param options - Optional configuration
 * 
 * @example
 * // In a detail page component:
 * const { item } = useItemData(id);
 * useTabTitleSync(item?.name);
 * 
 * // With split view disabled:
 * useTabTitleSync(item?.name, { disabled: isSplitView });
 */
export function useTabTitleSync(
  itemName: string | undefined, 
  options?: UseTabTitleSyncOptions
) {
  const { updateTabTitle, tabs } = useTabContext();
  const location = useLocation();
  const disabled = options?.disabled ?? false;
  
  useEffect(() => {
    if (disabled || !itemName) return;
    
    // Find the tab with the current path
    const activeTab = tabs.find(t => t.path === location.pathname);
    if (activeTab) {
      // Only update if the title is different to avoid unnecessary re-renders
      if (activeTab.title !== itemName) {
        updateTabTitle(activeTab.id, itemName);
      }
    }
  }, [disabled, itemName, location.pathname, tabs, updateTabTitle]);
}

export default useTabTitleSync;
